import { Room, RoomEvent, Track } from 'livekit-client';
import { useCallback, useEffect, useState } from 'react';

export const useTrackManagement = (room: Room | null) => {
  const [hostVideoTrack, setHostVideoTrack] = useState<Track | null>(null);
  const [hostAudioTrack, setHostAudioTrack] = useState<Track | null>(null);
  const [hostTracks, setHostTracks] = useState<Track[]>([]);

  const onTrackSubscribed = useCallback((track: Track) => {
    if (track.kind === Track.Kind.Video) {
      setHostVideoTrack(track);
    } else if (track.kind === Track.Kind.Audio) {
      setHostAudioTrack(track);
    }
  }, []);

  const onTrackUnsubscribed = useCallback((track: Track) => {
    // Detach the track from any media elements it was rendered into and clear
    // it from state so the audio/video element is cleaned up.
    track.detach();
    if (track.kind === Track.Kind.Video) {
      setHostVideoTrack(prev => (prev === track ? null : prev));
    } else if (track.kind === Track.Kind.Audio) {
      setHostAudioTrack(prev => (prev === track ? null : prev));
    }
  }, []);

  useEffect(() => {
    setHostTracks([...(hostVideoTrack ? [hostVideoTrack] : []), ...(hostAudioTrack ? [hostAudioTrack] : [])]);
  }, [hostVideoTrack, hostAudioTrack]);

  useEffect(() => {
    if (!room) return;
  
    room.remoteParticipants.forEach(participant => {
      participant.trackPublications.forEach(publication => {
        if (publication.track) {
          onTrackSubscribed(publication.track);
        }
      });
    });
  
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    return () => {
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    };
  }, [room, onTrackSubscribed, onTrackUnsubscribed]);

  return { hostTracks, hostVideoTrack, hostAudioTrack };
};
